import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { User } from '@supabase/supabase-js';
import { RootState } from '../store';
import { supabase, SUPABASE_ENABLED } from '../lib/supabase';
import { Tab } from '../App';
import { useT, LangToggle } from '../lib/i18n';
import { useDB } from '../lib/useDB';
import { SettingsModal } from './SettingsModal';

interface Props {
  tab: Tab;
  onTab: (t: Tab) => void;
  currentBankroll: number;
  totalProfit: number;
  user: User | null;
  plan: 'free' | 'pro';
  syncing: boolean;
  onUpgrade: () => void;
}

export function Header({ tab, onTab, currentBankroll, totalProfit, user, plan, syncing, onUpgrade }: Props) {
  const { saveSettings, resetAll } = useDB(user);
  const settings = useSelector((s: RootState) => s.settings);
  const { t } = useT();
  const [editingBankroll, setEditingBankroll] = useState(false);
  const [brInput, setBrInput] = useState('');
  const [showUser, setShowUser] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const tabs = [
    { id: 'dashboard' as Tab, label: t('nav.dashboard') },
    { id: 'new'       as Tab, label: t('nav.newSession'), cta: true },
    { id: 'import'    as Tab, label: t('nav.import') },
    { id: 'stats'     as Tab, label: t('nav.stats') },
    { id: 'sessions'  as Tab, label: t('nav.sessions') },
    { id: 'calendar'  as Tab, label: t('nav.calendar') },
    { id: 'goals'     as Tab, label: t('nav.goals') },
    { id: 'staking'   as Tab, label: t('nav.staking') },
    { id: 'guide'     as Tab, label: t('nav.guide') },
    { id: 'quiz'      as Tab, label: t('nav.dailyQuiz') },
    { id: 'blog'      as Tab, label: t('nav.blog') },
    { id: 'teams'     as Tab, label: t('teams.navLabel') },
  ];

  const handleBankrollSave = () => {
    const val = parseFloat(brInput);
    if (!isNaN(val)) saveSettings({ initialBankroll: val });
    setEditingBankroll(false);
  };

  const handleReset = () => {
    if (window.confirm('¿Borrar TODAS las sesiones? / Delete ALL sessions?')) resetAll();
  };

  const handleSignOut = async () => {
    if (SUPABASE_ENABLED) await supabase.auth.signOut();
  };

  const handleOpenSettings = () => {
    setShowUser(false);
    setShowSettings(true);
  };

  const fmt = (n: number) => `${n >= 0 ? '+' : '-'}$${Math.abs(n).toFixed(2)}`;
  const avatar = user?.email?.charAt(0).toUpperCase() ?? '?';

  return (
    <header className="app-header">
      <div className="header-top">
        <div className="header-brand">
          <span className="brand-suit">◈</span>
          <span className="brand-name">GRIND<span className="brand-accent">LOG</span></span>
          {plan === 'pro'
            ? <span className="plan-badge">{t('common.pro')}</span>
            : <button className="btn-upgrade-pill" onClick={onUpgrade}>{t('header.upgradePro')}</button>
          }
          {syncing && <span className="sync-dot" title={t('header.syncing')} />}
        </div>

        <div className="header-bankroll">
          <div className="bankroll-item">
            <span className="bankroll-label">{t('header.bankroll')}</span>
            {editingBankroll ? (
              <div className="bankroll-edit">
                <input autoFocus type="number" value={brInput}
                  onChange={e => setBrInput(e.target.value)}
                  onBlur={handleBankrollSave}
                  onKeyDown={e => e.key === 'Enter' && handleBankrollSave()} />
              </div>
            ) : (
              <button className="bankroll-value" onClick={() => { setBrInput(String(settings.initialBankroll)); setEditingBankroll(true); }}>
                ${currentBankroll.toFixed(2)}
              </button>
            )}
          </div>
          <div className="bankroll-divider" />
          <div className="bankroll-item">
            <span className="bankroll-label">{t('header.pnlTotal')}</span>
            <span className={`bankroll-pnl ${totalProfit >= 0 ? 'pos' : 'neg'}`}>{fmt(totalProfit)}</span>
          </div>
          <button className="btn-reset-hdr" onClick={handleReset} title={t('header.resetData')}>↺</button>

          <LangToggle />

          {SUPABASE_ENABLED && user && (
            <div className="user-menu-wrap" style={{ position: 'relative' }}>
              <button className="user-avatar" onClick={() => setShowUser(v => !v)}>{avatar}</button>
              {showUser && (
                <div className="user-dropdown">
                  <div className="ud-email">{user.email}</div>
                  <div className="ud-plan">{t('header.plan')}: <strong>{plan.toUpperCase()}</strong></div>

                  <div className="ud-divider" />

                  <button type="button" className="ud-link" onClick={handleOpenSettings}>
                    <span className="ud-link-icon" aria-hidden>⚙</span>
                    {t('header.settings')}
                  </button>

                  <div className="ud-divider" />

                  <button className="ud-signout" onClick={handleSignOut}>{t('header.logout')}</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <nav className="header-nav">
        {tabs.map(tb => (
          <button key={tb.id}
            className={`nav-tab ${tab === tb.id ? 'active' : ''} ${tb.cta ? 'tab-cta' : ''}`}
            onClick={() => onTab(tb.id)}>
            {tb.label}
          </button>
        ))}
      </nav>

      <SettingsModal user={user} isOpen={showSettings} onClose={() => setShowSettings(false)} />
    </header>
  );
}
